<template>
  <div>
    <!-- Header with Tabs -->
    <div class="employee-form-header">
      <ActiveTabComponent
        :tabs="employeeTabs"
        :defaultTab="'addemployee'"
        @tab-changed="handleTabChange"
      >
        <template #tab-buttons>
          <v-btn icon @click="cancel">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </template>
        <template #addemployee>
          <div class="form-content">
            <v-form ref="form" v-model="valid" lazy-validation>
              <!-- Profile Section -->
              <div class="form-section form-content-border">
                <h4 class="section-title">
                  <v-icon color="primary" class="mr-2">mdi-account-outline</v-icon>
                  Profile Information
                </h4>
                <div class="section-content">
                  <v-row>
                    <v-col cols="12" md="4" class="py-0">
                      <v-text-field
                        outlined
                        dense
                        v-model="form.first_name"
                        :rules="[(v) => !!v || 'First Name is required']"
                        label="First Name *"
                        placeholder="ex: John"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="4" class="py-0">
                      <v-text-field
                        outlined
                        dense
                        v-model="form.middle_name"
                        label="Middle Name"
                        placeholder="ex: M"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="4" class="py-0">
                      <v-text-field
                        outlined
                        dense
                        v-model="form.last_name"
                        label="Last Name"
                        placeholder="ex: Doe"
                      ></v-text-field>
                    </v-col>
                    <!-- Add Gender field -->
                    <v-col cols="12" md="4" class="py-0">
                      <v-select
                        outlined
                        dense
                        v-model="form.gender"
                        :items="genderOptions"
                        item-text="text"
                        item-value="value"
                        label="Gender"
                        placeholder="Select Gender"
                      ></v-select>
                    </v-col>
                    <!-- Add Marital Status field -->
                    <v-col cols="12" md="4" class="py-0">
                      <v-select
                        outlined
                        dense
                        v-model="form.maritalStatus"
                        :items="maritalStatusOptions"
                        item-text="text"
                        item-value="value"
                        label="Marital Status"
                        placeholder="Select Marital Status"
                      ></v-select>
                    </v-col>
                    <v-col cols="12" md="4" class="py-0">
                      <v-text-field
                        outlined
                        dense
                        v-model="form.dob"
                        label="Date of Birth"
                        type="date"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="4" class="py-0">
                      <v-text-field
                        outlined
                        dense
                        v-model="form.mobile"
                        label="Mobile "
                        type="number"
                        :rules="[
                          (v) =>
                            !v ||
                            /^[0-9]{10}$/.test(v) ||
                            'Please enter a valid 10-digit mobile number',
                          () => mobileOrEmailRequired()
                        ]"
                        :disabled="isDisabled || isMobileFieldDisabled"
                        placeholder="ex: 9876543210"
                        @input="debouncedCheckMobileAvailability"
                      >
                        <template v-slot:append v-if="mobileChecking">
                          <Loader :size="3" />
                        </template>
                        <template v-slot:append v-else-if="mobileChecked && !mobileAvailable">
                          <v-icon color="error" small>mdi-close-circle</v-icon>
                        </template>
                        <template v-slot:append v-else-if="mobileChecked && mobileAvailable">
                          <v-icon color="success" small>mdi-check-circle</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                    <v-col cols="12" md="4" class="py-0">
                      <v-text-field
                        outlined
                        dense
                        v-model="form.email"
                        label="Email"
                        :rules="[
                          (v) => !v || /.+@.+\..+/.test(v) || 'E-mail must be valid',
                          () => mobileOrEmailRequired()
                        ]"
                        :disabled="isDisabled || isEmailFieldDisabled"
                        placeholder="ex: john.doe@example.com"
                        @input="debouncedCheckEmailAvailability"
                      >
                        <template v-slot:append v-if="emailChecking">
                          <Loader :size="3" />
                        </template>
                        <template v-slot:append v-else-if="emailChecked && !emailAvailable">
                          <v-icon color="error" small>mdi-close-circle</v-icon>
                        </template>
                        <template v-slot:append v-else-if="emailChecked && emailAvailable">
                          <v-icon color="success" small>mdi-check-circle</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>
                  <!-- Consolidated validation messages for mobile and email -->
                  <div class="w-full">
                    <div
                      v-for="error in validationErrors"
                      :key="error"
                      class="text-caption error--text mb-2"
                    >
                      {{ error }}
                    </div>
                  </div>
                </div>
              </div>
              <!-- Address Section -->
              <div class="form-section form-content-border">
                <h4 class="section-title">
                  <v-icon color="primary" class="mr-2">mdi-map-marker</v-icon>
                  Communication Address (Optional)
                </h4>
                <div class="section-content">
                  <v-row>
                    <v-col cols="12" class="py-0">
                      <v-text-field
                        outlined
                        dense
                        v-model="form.address.permanent.addressLine1"
                        label="Address Line 1"
                        placeholder="ex: 123 Main Street"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6" class="py-0">
                      <v-text-field
                        outlined
                        dense
                        v-model="form.address.permanent.city"
                        label="City"
                        placeholder="ex: Mumbai"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6" class="py-0">
                      <v-text-field
                        outlined
                        dense
                        v-model="form.address.permanent.state"
                        label="State"
                        placeholder="ex: Maharashtra"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6" class="py-0">
                      <v-text-field
                        outlined
                        dense
                        v-model="form.address.permanent.country"
                        label="Country"
                        placeholder="ex: India"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6" class="py-0">
                      <v-text-field
                        outlined
                        dense
                        v-model="form.address.permanent.pinCode"
                        label="Pin Code"
                        type="number"
                        :rules="[
                          (v) => !v || /^\d{6}$/.test(v) || 'Please enter a valid 6-digit pin code'
                        ]"
                        placeholder="ex: 400001"
                      ></v-text-field>
                    </v-col>
                  </v-row>
                </div>
              </div>
              <!-- Current Address Section -->
              <div v-if="!form.address.sameAsPermanent" class="form-section form-content-border">
                <h4 class="section-title">
                  <v-icon color="primary" class="mr-2">mdi-map-marker-multiple</v-icon>
                  Current Address
                </h4>
                <div class="section-content">
                  <v-row>
                    <v-col cols="12" class="py-0">
                      <CommonAutocomplete
                        :addressLine1="currentAddressLine1"
                        v-model="currentSelectedAddress"
                        @selected-place="handleSelectedPlaceCurrent"
                      />
                    </v-col>
                    <v-col cols="12" md="6" class="py-0">
                      <v-text-field
                        outlined
                        dense
                        v-model="form.address.current.city"
                        label="City"
                        placeholder="ex: Mumbai"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6" class="py-0">
                      <v-text-field
                        outlined
                        dense
                        v-model="form.address.current.state"
                        label="State"
                        placeholder="ex: Maharashtra"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6" class="py-0">
                      <v-text-field
                        outlined
                        dense
                        v-model="form.address.current.country"
                        label="Country"
                        placeholder="ex: India"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6" class="py-0">
                      <v-text-field
                        outlined
                        dense
                        v-model="form.address.current.pinCode"
                        label="Pin Code"
                        type="number"
                        :rules="[
                          (v) => !v || /^\d{6}$/.test(v) || 'Please enter a valid 6-digit pin code'
                        ]"
                        placeholder="ex: 400001"
                      ></v-text-field>
                    </v-col>
                  </v-row>
                </div>
              </div>

              <!-- Additional Info Section -->
              <div class="form-section form-content-border">
                <h4 class="section-title">
                  <v-icon color="primary" class="mr-2">mdi-information-outline</v-icon>
                  Additional Info
                </h4>
                <div class="section-content">
                  <v-row>
                    <v-col cols="12" md="6" class="py-0">
                      <v-text-field
                        outlined
                        dense
                        v-model="form.bankName"
                        label="Bank Name"
                        placeholder="ex: SBI Bank"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6" class="py-0">
                      <v-text-field
                        outlined
                        dense
                        v-model="form.ifscCode"
                        label="IFSC Code"
                        placeholder="ex: HDFC0000123"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6" class="py-0">
                      <v-text-field
                        outlined
                        dense
                        v-model="form.bankAccountNumber"
                        label="Bank Account Number"
                        placeholder="ex: 123456789012"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6" class="py-0">
                      <v-text-field
                        outlined
                        dense
                        v-model="form.panNumber"
                        label="PAN Number"
                        placeholder="ex: ABCDE1234F"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6" class="py-0">
                      <v-text-field
                        outlined
                        dense
                        v-model="form.aadharNumber"
                        label="Aadhar Number"
                        placeholder="ex: 1234 5678 9012"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6" class="py-0">
                      <v-text-field
                        outlined
                        dense
                        v-model="form.pfNumber"
                        label="PF Number"
                        placeholder="ex: MH/0123/123456789"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6" class="py-0">
                      <v-text-field
                        outlined
                        dense
                        v-model="form.pfUAN"
                        label="PF UAN"
                        placeholder="ex: 123456789012"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6" class="py-0">
                      <v-text-field
                        outlined
                        dense
                        v-model="form.esicNumber"
                        label="ESIC Number"
                        placeholder="ex: 123456789012345"
                      ></v-text-field>
                    </v-col>
                  </v-row>
                </div>
              </div>

              <!-- Employment Details Section -->
              <div class="form-section form-content-border">
                <h4 class="section-title">
                  <v-icon color="primary" class="mr-2">mdi-briefcase-outline</v-icon>
                  Employment Details
                </h4>
                <div class="section-content">
                  <v-expansion-panels multiple accordion>
                    <!-- Joining Details -->
                    <v-expansion-panel>
                      <v-expansion-panel-header>Joining Details</v-expansion-panel-header>
                      <v-expansion-panel-content>
                        <v-row>
                          <v-col cols="12" md="4" class="py-0">
                            <v-text-field
                              v-model="form.employmentDetails.joiningDetails.joinedOn"
                              label="Joined On"
                              type="date"
                              outlined
                              dense
                            ></v-text-field>
                          </v-col>
                          <v-col cols="12" md="4" class="py-0">
                            <v-text-field
                              v-model="form.employmentDetails.joiningDetails.joinedAs"
                              label="Joined As"
                              outlined
                              dense
                            ></v-text-field>
                          </v-col>
                          <v-col cols="12" md="4" class="py-0">
                            <v-text-field
                              v-model="form.employmentDetails.joiningDetails.confirmationDate"
                              label="Confirmation Date"
                              type="date"
                              outlined
                              dense
                            ></v-text-field>
                          </v-col>
                          <v-col cols="12" md="4" class="py-0">
                            <v-select
                              v-model="form.employmentDetails.joiningDetails.status"
                              :items="employmentStatuses"
                              label="Status"
                              outlined
                              dense
                            ></v-select>
                          </v-col>
                          <v-col cols="12" md="4" class="py-0">
                            <v-text-field
                              v-model="form.employmentDetails.joiningDetails.probationPeriod"
                              label="Probation Period"
                              outlined
                              dense
                            ></v-text-field>
                          </v-col>
                          <v-col cols="12" md="4" class="py-0">
                            <v-text-field
                              v-model="form.employmentDetails.joiningDetails.noticePeriod"
                              label="Notice Period"
                              outlined
                              dense
                            ></v-text-field>
                          </v-col>
                          <v-col cols="12" md="4" class="py-0">
                            <v-autocomplete
                              v-model="form.employmentDetails.joiningDetails.referredBy"
                              :items="fullNameManagers"
                              item-text="fullName"
                              item-value="_id"
                              label="Referred By"
                              outlined
                              dense
                              clearable
                            ></v-autocomplete>
                          </v-col>
                        </v-row>
                      </v-expansion-panel-content>
                    </v-expansion-panel>

                    <!-- Work History -->
                    <v-expansion-panel>
                      <v-expansion-panel-header>Work History</v-expansion-panel-header>
                      <v-expansion-panel-content>
                        <div
                          v-for="(item, index) in form.employmentDetails.workHistory"
                          :key="index"
                          class="mb-4 pa-4 border rounded"
                        >
                          <div class="d-flex justify-end">
                            <v-btn
                              icon
                              small
                              color="error"
                              @click="removeItem('workHistory', index)"
                              ><v-icon>mdi-delete</v-icon></v-btn
                            >
                          </div>
                          <v-row>
                            <v-col cols="12" md="4" class="py-0">
                              <v-text-field
                                v-model="item.employerName"
                                label="Employer Name"
                                outlined
                                dense
                              ></v-text-field>
                            </v-col>
                            <v-col cols="12" md="4" class="py-0">
                              <v-text-field
                                v-model="item.designation"
                                label="Designation"
                                outlined
                                dense
                              ></v-text-field>
                            </v-col>
                            <v-col cols="12" md="4" class="py-0">
                              <v-text-field
                                v-model="item.joiningDate"
                                label="Joining Date"
                                type="date"
                                outlined
                                dense
                              ></v-text-field>
                            </v-col>
                            <v-col cols="12" md="4" class="py-0">
                              <v-text-field
                                v-model="item.exitDate"
                                label="Exit Date"
                                type="date"
                                outlined
                                dense
                              ></v-text-field>
                            </v-col>
                            <v-col cols="12" md="4" class="py-0">
                              <v-text-field
                                v-model="item.reasonForLeaving"
                                label="Reason For Leaving"
                                outlined
                                dense
                              ></v-text-field>
                            </v-col>
                          </v-row>
                        </div>
                        <v-btn outlined color="primary" @click="addItem('workHistory')" small
                          >Add Work History</v-btn
                        >
                      </v-expansion-panel-content>
                    </v-expansion-panel>

                    <!-- Education -->
                    <v-expansion-panel>
                      <v-expansion-panel-header>Education</v-expansion-panel-header>
                      <v-expansion-panel-content>
                        <div
                          v-for="(item, index) in form.employmentDetails.education"
                          :key="index"
                          class="mb-4 pa-4 border rounded"
                        >
                          <div class="d-flex justify-end">
                            <v-btn icon small color="error" @click="removeItem('education', index)"
                              ><v-icon>mdi-delete</v-icon></v-btn
                            >
                          </div>
                          <v-row>
                            <v-col cols="12" md="4" class="py-0">
                              <v-text-field
                                v-model="item.degree"
                                label="Degree"
                                outlined
                                dense
                              ></v-text-field>
                            </v-col>
                            <v-col cols="12" md="4" class="py-0">
                              <v-text-field
                                v-model="item.institute"
                                label="Institute"
                                outlined
                                dense
                              ></v-text-field>
                            </v-col>
                            <v-col cols="12" md="4" class="py-0">
                              <v-text-field
                                v-model="item.grade"
                                label="Grade"
                                outlined
                                dense
                              ></v-text-field>
                            </v-col>
                            <v-col cols="12" md="4" class="py-0">
                              <v-text-field
                                v-model="item.qualificationArea"
                                label="Qualification Area"
                                outlined
                                dense
                              ></v-text-field>
                            </v-col>
                            <v-col cols="12" md="4" class="py-0">
                              <v-text-field
                                v-model="item.from"
                                label="From"
                                type="date"
                                outlined
                                dense
                              ></v-text-field>
                            </v-col>
                            <v-col cols="12" md="4" class="py-0">
                              <v-text-field
                                v-model="item.to"
                                label="To"
                                type="date"
                                outlined
                                dense
                              ></v-text-field>
                            </v-col>
                            <v-col cols="12" md="4" class="py-0">
                              <v-text-field
                                v-model="item.remarks"
                                label="Remarks"
                                outlined
                                dense
                              ></v-text-field>
                            </v-col>
                          </v-row>
                        </div>
                        <v-btn outlined color="primary" @click="addItem('education')" small
                          >Add Education</v-btn
                        >
                      </v-expansion-panel-content>
                    </v-expansion-panel>

                    <!-- Identity Details -->
                    <v-expansion-panel>
                      <v-expansion-panel-header>Identity Details</v-expansion-panel-header>
                      <v-expansion-panel-content>
                        <div
                          v-for="(item, index) in form.employmentDetails.identityDetails"
                          :key="index"
                          class="mb-4 pa-4 border rounded"
                        >
                          <div class="d-flex justify-end">
                            <v-btn
                              icon
                              small
                              color="error"
                              @click="removeItem('identityDetails', index)"
                              ><v-icon>mdi-delete</v-icon></v-btn
                            >
                          </div>
                          <v-row>
                            <v-col cols="12" md="4" class="py-0">
                              <v-select
                                v-model="item.idType"
                                :items="idTypes"
                                label="ID Type"
                                outlined
                                dense
                              ></v-select>
                            </v-col>
                            <v-col cols="12" md="4" class="py-0">
                              <v-text-field
                                v-model="item.idNumber"
                                label="ID Number"
                                outlined
                                dense
                              ></v-text-field>
                            </v-col>
                            <v-col cols="12" md="4" class="py-0">
                              <v-text-field
                                v-model="item.nameAsperId"
                                label="Name as per ID"
                                outlined
                                dense
                              ></v-text-field>
                            </v-col>
                            <v-col cols="12" md="4" class="py-0">
                              <v-text-field
                                v-model="item.expiryDate"
                                label="Expiry Date"
                                type="date"
                                outlined
                                dense
                              ></v-text-field>
                            </v-col>
                            <v-col cols="12" md="4" class="py-0">
                              <v-text-field
                                v-model="item.issuedBy"
                                label="Issued By"
                                outlined
                                dense
                              ></v-text-field>
                            </v-col>
                            <v-col cols="12" md="4" class="py-0">
                              <v-text-field
                                v-model="item.issueDate"
                                label="Issue Date"
                                type="date"
                                outlined
                                dense
                              ></v-text-field>
                            </v-col>
                            <v-col cols="12" md="4" class="py-0">
                              <v-checkbox v-model="item.isVerfied" label="Verified"></v-checkbox>
                            </v-col>
                            <v-col cols="12" md="4" class="py-0" v-if="item.isVerfied">
                              <v-text-field
                                v-model="item.verifiedBy"
                                label="Verified By"
                                outlined
                                dense
                              ></v-text-field>
                            </v-col>
                            <v-col cols="12" md="4" class="py-0" v-if="item.isVerfied">
                              <v-text-field
                                v-model="item.verifiedOn"
                                label="Verified On"
                                type="date"
                                outlined
                                dense
                              ></v-text-field>
                            </v-col>

                            <v-col cols="12" md="4" class="py-0">
                              <v-text-field
                                v-model="item.bankName"
                                label="Bank Name"
                                outlined
                                dense
                              ></v-text-field>
                            </v-col>
                            <v-col cols="12" md="4" class="py-0">
                              <v-text-field
                                v-model="item.accountNumber"
                                label="Account Number"
                                type="number"
                                outlined
                                dense
                              ></v-text-field>
                            </v-col>
                            <v-col cols="12" md="4" class="py-0">
                              <v-text-field
                                v-model="item.ifscCode"
                                label="IFSC Code"
                                outlined
                                dense
                              ></v-text-field>
                            </v-col>
                            <v-col cols="12" md="4" class="py-0">
                              <v-text-field
                                v-model="item.branchName"
                                label="Branch Name"
                                outlined
                                dense
                              ></v-text-field>
                            </v-col>
                          </v-row>
                        </div>
                        <v-btn outlined color="primary" @click="addItem('identityDetails')" small
                          >Add Identity Details</v-btn
                        >
                      </v-expansion-panel-content>
                    </v-expansion-panel>

                    <!-- Background Verification -->
                    <v-expansion-panel>
                      <v-expansion-panel-header>Background Verification</v-expansion-panel-header>
                      <v-expansion-panel-content>
                        <v-row>
                          <v-col cols="12" md="4" class="py-0">
                            <v-select
                              v-model="form.employmentDetails.backgroundVerification.status"
                              :items="bgStatuses"
                              label="Status"
                              outlined
                              dense
                            ></v-select>
                          </v-col>
                          <v-col cols="12" md="4" class="py-0">
                            <v-text-field
                              v-model="form.employmentDetails.backgroundVerification.verifiedBy"
                              label="Verified By (Agency)"
                              outlined
                              dense
                            ></v-text-field>
                          </v-col>
                          <v-col cols="12" md="4" class="py-0">
                            <v-text-field
                              v-model="form.employmentDetails.backgroundVerification.verifiedOn"
                              label="Verified On"
                              type="date"
                              outlined
                              dense
                            ></v-text-field>
                          </v-col>
                          <v-col cols="12" md="12" class="py-0">
                            <v-textarea
                              v-model="form.employmentDetails.backgroundVerification.remarks"
                              label="Remarks"
                              outlined
                              dense
                              rows="2"
                            ></v-textarea>
                          </v-col>
                        </v-row>
                      </v-expansion-panel-content>
                    </v-expansion-panel>

                    <!-- Remarks -->
                    <v-expansion-panel>
                      <v-expansion-panel-header>Remarks</v-expansion-panel-header>
                      <v-expansion-panel-content>
                        <v-row>
                          <v-col cols="12" class="py-0">
                            <v-textarea
                              v-model="form.employmentDetails.remarks"
                              label="Generic Remarks"
                              outlined
                              dense
                              rows="3"
                            ></v-textarea>
                          </v-col>
                        </v-row>
                      </v-expansion-panel-content>
                    </v-expansion-panel>

                    <!-- Contact & Emergency -->
                    <v-expansion-panel>
                      <v-expansion-panel-header
                        >Contact & Emergency Contact</v-expansion-panel-header
                      >
                      <v-expansion-panel-content>
                        <h4 class="font-weight-bold mb-2">Contact</h4>
                        <v-row>
                          <v-col cols="12" md="4" class="py-0">
                            <v-text-field
                              v-model="form.employmentDetails.contact.name"
                              label="Name"
                              outlined
                              dense
                            ></v-text-field>
                          </v-col>
                          <v-col cols="12" md="4" class="py-0">
                            <v-text-field
                              v-model="form.employmentDetails.contact.contactNumber"
                              label="Contact Number"
                              outlined
                              dense
                            ></v-text-field>
                          </v-col>
                          <v-col cols="12" md="4" class="py-0">
                            <v-text-field
                              v-model="form.employmentDetails.contact.email"
                              label="Email"
                              outlined
                              dense
                            ></v-text-field>
                          </v-col>
                          <v-col cols="12" class="py-0"><h6 class="mb-2">Address</h6></v-col>
                          <v-col cols="12" class="py-0">
                            <v-text-field
                              v-model="form.employmentDetails.contact.address.addressLine1"
                              label="Address Line 1"
                              outlined
                              dense
                            ></v-text-field>
                          </v-col>
                          <v-col cols="6" class="py-0">
                            <v-text-field
                              v-model="form.employmentDetails.contact.address.city"
                              label="City"
                              outlined
                              dense
                            ></v-text-field>
                          </v-col>
                          <v-col cols="6" class="py-0">
                            <v-text-field
                              v-model="form.employmentDetails.contact.address.state"
                              label="State"
                              outlined
                              dense
                            ></v-text-field>
                          </v-col>
                          <v-col cols="6" class="py-0">
                            <v-text-field
                              v-model="form.employmentDetails.contact.address.country"
                              label="Country"
                              outlined
                              dense
                            ></v-text-field>
                          </v-col>
                          <v-col cols="6" class="py-0">
                            <v-text-field
                              v-model="form.employmentDetails.contact.address.pinCode"
                              label="Pin Code"
                              outlined
                              dense
                            ></v-text-field>
                          </v-col>
                        </v-row>
                        <v-divider class="my-4"></v-divider>
                        <h4 class="font-weight-bold mb-2">Emergency Contact</h4>
                        <v-row>
                          <v-col cols="12" md="4" class="py-0">
                            <v-text-field
                              v-model="form.employmentDetails.emergencyContact.name"
                              label="Name"
                              outlined
                              dense
                            ></v-text-field>
                          </v-col>
                          <v-col cols="12" md="4" class="py-0">
                            <v-text-field
                              v-model="form.employmentDetails.emergencyContact.contactNumber"
                              label="Contact Number"
                              outlined
                              dense
                            ></v-text-field>
                          </v-col>
                          <v-col cols="12" md="4" class="py-0">
                            <v-text-field
                              v-model="form.employmentDetails.emergencyContact.email"
                              label="Email"
                              outlined
                              dense
                            ></v-text-field>
                          </v-col>
                          <v-col cols="12" class="py-0"><h6 class="mb-2">Address</h6></v-col>
                          <v-col cols="12" class="py-0">
                            <v-text-field
                              v-model="form.employmentDetails.emergencyContact.address.addressLine1"
                              label="Address Line 1"
                              outlined
                              dense
                            ></v-text-field>
                          </v-col>
                          <v-col cols="6" class="py-0">
                            <v-text-field
                              v-model="form.employmentDetails.emergencyContact.address.city"
                              label="City"
                              outlined
                              dense
                            ></v-text-field>
                          </v-col>
                          <v-col cols="6" class="py-0">
                            <v-text-field
                              v-model="form.employmentDetails.emergencyContact.address.state"
                              label="State"
                              outlined
                              dense
                            ></v-text-field>
                          </v-col>
                          <v-col cols="6" class="py-0">
                            <v-text-field
                              v-model="form.employmentDetails.emergencyContact.address.country"
                              label="Country"
                              outlined
                              dense
                            ></v-text-field>
                          </v-col>
                          <v-col cols="6" class="py-0">
                            <v-text-field
                              v-model="form.employmentDetails.emergencyContact.address.pinCode"
                              label="Pin Code"
                              outlined
                              dense
                            ></v-text-field>
                          </v-col>
                        </v-row>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </div>
              </div>

              <!-- Corporate Expense Section -->
              <!-- <div
                v-if="orgConfig?.organization_config?.is_corporate_expense_enabled"
                class="form-section form-content-border"
              >
                <h4 class="section-title">
                  <v-icon color="primary" class="mr-2">mdi-credit-card</v-icon>
                  Corporate Expense
                </h4>
                <div class="section-content">
                  <v-row>
                    <v-col cols="12">
                      <v-checkbox
                        :color="$vuetify.theme.currentTheme.primary"
                        v-model="form.is_corporate_expense_enabled"
                        label="Enable Corporate Expenses"
                      ></v-checkbox>
                    </v-col>
                    <v-col v-if="form.is_corporate_expense_enabled" cols="12" md="6">
                      <v-text-field
                        outlined
                        dense
                        v-model="form.max_corporate_expense_limit"
                        label="Corporate expenses limit"
                        type="number"
                        :rules="[(v) => !v || v > 0 || 'Amount must be greater than 0']"
                        placeholder="ex: 50000"
                      ></v-text-field>
                    </v-col>
                  </v-row>
                </div>
              </div> -->

              <!-- Management Assignment Section -->
              <div class="form-section form-content-border">
                <h4 class="section-title">
                  <v-icon color="primary" class="mr-2">mdi-account-supervisor</v-icon>
                  Management
                </h4>
                <div class="section-content">
                  <v-row>
                    <v-col cols="12" md="6" class="py-0">
                      <v-text-field
                        outlined
                        dense
                        v-model="form.joiningDate"
                        label="Joining Date"
                        type="date"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6" class="py-0" v-if="empData">
                      <v-text-field
                        outlined
                        dense
                        v-model="form.exitDate"
                        label="Exit Date"
                        type="date"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6" class="py-0">
                      <v-text-field
                        outlined
                        dense
                        v-model="form.employee_id"
                        label="Employee ID *"
                        :disabled="isDisabled"
                        :rules="[
                          (v) => !!v || 'Employee ID is required',
                          (v) => /^\w+$/.test(v) || 'Employee ID must be alphanumeric'
                        ]"
                        clearable
                        placeholder="ex: EMP001"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6" class="py-0">
                      <v-select
                        :items="employeeTypes"
                        v-model="form.employee_type"
                        label="Employee Type *"
                        item-text="name"
                        item-value="name"
                        dense
                        outlined
                        :rules="[(v) => !!v || 'Employee Type is required']"
                        clearable
                        placeholder="ex: Full Time"
                      ></v-select>
                    </v-col>
                    <v-col cols="12" md="6" class="py-0">
                      <v-select
                        v-model="selectedDepartment"
                        :items="departments"
                        item-text="departmentName"
                        item-value="_id"
                        label="Department"
                        outlined
                        dense
                        clearable
                        @change="onDepartmentChange"
                        placeholder="Select Department"
                      ></v-select>
                      <!-- <v-text-field
                        v-else
                        outlined
                        dense
                        v-model="form.department"
                        label="Department"
                        placeholder="ex: Sales"
                      ></v-text-field> -->
                    </v-col>
                    <v-col cols="12" md="6" class="py-0">
                      <v-select
                        :disabled="!selectedDepartment"
                        v-model="selectedDesignation"
                        :items="filteredDesignations"
                        item-text="designationName"
                        item-value="_id"
                        label="Designation"
                        outlined
                        dense
                        clearable
                        placeholder="Select Designation"
                      ></v-select>
                      <!-- <v-text-field
                        v-else
                        outlined
                        dense
                        v-model="form.designation"
                        label="Designation"
                        placeholder="ex: Sales Executive"
                      ></v-text-field> -->
                    </v-col>
                    <v-col cols="12" md="6" class="py-0">
                      <v-text-field
                        outlined
                        dense
                        v-model="form.salary"
                        label="Salary(CTC)"
                        type="number"
                        :rules="[
                          (v) => !v || /^\d+$/.test(v) || 'Please enter a valid salary amount'
                        ]"
                        placeholder="ex: 30000"
                      ></v-text-field>
                    </v-col>
                  </v-row>

                  <v-divider class="mb-6"></v-divider>

                  <v-row>
                    <v-col cols="12" md="6" class="py-0">
                      <v-autocomplete
                        v-model="selectedRole"
                        :items="roleList"
                        item-text="role"
                        item-value="_id"
                        label="Assign Role"
                        clearable
                        return-object
                        outlined
                        dense
                        placeholder="Select Role"
                      ></v-autocomplete>
                    </v-col>

                    <v-col cols="12" md="6" class="py-0">
                      <v-text-field
                        outlined
                        dense
                        v-model="form.effectiveDate"
                        label="Effective Date"
                        type="date"
                      ></v-text-field>
                    </v-col>
                  </v-row>
                  <v-divider class="mb-6"></v-divider>

                  <v-row>
                    <v-col cols="12" md="6" class="py-0">
                      <v-select
                        v-model="selectedBranch"
                        :items="branches"
                        item-text="branchName"
                        item-value="_id"
                        label="Link Branch"
                        outlined
                        dense
                      >
                      </v-select>
                    </v-col>
                    <v-col cols="12" md="6" class="py-0">
                      <v-text-field
                        outlined
                        dense
                        v-model="form.workLocation"
                        label="Work Location"
                        placeholder="ex: Mumbai"
                        disabled
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6" class="py-0">
                      <v-select
                        v-model="selectedTeam"
                        :disabled="!selectedBranch"
                        :items="filteredTeams"
                        item-text="teamName"
                        item-value="_id"
                        label="Link Team"
                        outlined
                        dense
                      >
                      </v-select>
                    </v-col>
                    <v-col cols="12" md="6" class="py-0">
                      <v-autocomplete
                        v-model="selectedManager"
                        :items="fullNameManagers"
                        item-text="fullName"
                        item-value="_id"
                        label="Assign Manager"
                        return-object
                        outlined
                        dense
                        placeholder="Select Reportee"
                      ></v-autocomplete>
                    </v-col>
                  </v-row>
                </div>
              </div>
            </v-form>

            <!-- Form Actions -->
          </div>
        </template>
        <template #bulk>
          <GenericBulkUpload
            ref="bulkUploader"
            :title="'Bulk Employee Upload'"
            :subtitle="'Upload multiple employees using Excel file'"
            :entityName="'Employee'"
            :uploadEndpoint="'/user/bulk'"
            :requiredColumns="['first_name', 'last_name', 'mobile', 'employee_id', 'employee_type']"
            :sampleData="[
              {
                first_name: 'John',
                last_name: 'Doe',
                mobile: '9876543210',
                email: 'john.doe@example.com',
                managerId: 'MANAGER001',
                employee_id: 'EMP001839',
                employee_type: 'Full Time',
                department: 'Sales',
                role: 'Manager',
                designation: 'Sales Executive',
                addressLine1: '123 Main Street',
                city: 'Mumbai',
                state: 'Maharashtra',
                country: 'India',
                pinCode: '400001',
                dob: '1990-01-01'
              }
            ]"
            :successKey="'usersCreated'"
            :failedKey="'usersFailed'"
            :invalidItemsKey="'invalidUsers'"
            :additionalPayload="{ organization_id: userData.organization_id }"
            :method="'post'"
            :enableSave="false"
            :controlParentSaveButton="true"
            @save-button-visibility="handleSaveButtonVisibility"
            @upload-complete="handleBulkUploadComplete"
          />
        </template>
      </ActiveTabComponent>
    </div>

    <div
      class="sticky bottom-0 bg-white py-4 px-6 flex justify-end border-t items-center space-x-2"
    >
      <v-btn @click="cancel()" outlined class="cancel-btn normal-case"> Cancel </v-btn>
      <v-btn
        v-if="isSaveButtonEnabled"
        @click="saveEmployee()"
        :color="$vuetify.theme.currentTheme.primary"
        class="save-btn normal-case white--text"
      >
        <v-icon left>mdi-content-save</v-icon>
        Save
      </v-btn>
    </div>
  </div>
</template>
<script>
import CommonAutocomplete from '~/components/CommonAutoComplete.vue'
import ActiveTabComponent from '~/components/ActitiveTabcomponent.vue'
import GenericBulkUpload from '../CollectionManagement/GenericBulkUpload.vue'
import { employeeTypes } from '@/utils/workforce/constants'
import { debounce } from '@/utils/common'
import Loader from '@/components/UI/Loader.vue'
import { axiosUrl } from '@/utils/workforceApi'

export default {
  name: 'EmployeesTabAddComponent',
  props: [
    // 'companyCode',
    'disabled',
    'empData',
    'orgConfig',
    'updateEmployee',
    'managersList',
    'roleList',
    'financialPartnerType',
    'branches',
    'teams'
  ],
  components: {
    CommonAutocomplete,
    ActiveTabComponent,
    GenericBulkUpload,
    Loader
  },
  data() {
    return {
      activeType: 'addemployee',
      isSaveButtonEnabled: true,
      permanetAddressLine1: '',
      permanetSelectedAddress: '',
      currentAddressLine1: '',
      currentSelectedAddress: '',
      employee: this.empData,
      userData: this.$storage.getUniversal('user'),
      selectedManager: null,
      selectedRole: null,
      employeeTypes: [],
      selectedBranch: null,
      selectedTeam: null,
      // Add department and designation related properties
      departments: [],
      designations: [],
      selectedDepartment: null,
      selectedDesignation: null,
      // Add gender options
      genderOptions: [
        { text: 'Male', value: 'Male' },
        { text: 'Female', value: 'Female' }
      ],
      // Add marital status options
      maritalStatusOptions: [
        { text: 'Married', value: 'Married' },
        { text: 'Unmarried', value: 'Unmarried' }
      ],
      employmentStatuses: ['Confirmed', 'Probation', 'Contractor', 'Notice Period', 'Intern'],
      idTypes: ['Aadhar', 'PAN', 'Driving License', 'Passport', 'Voter ID'],
      bgStatuses: ['pending', 'in-progress', 'completed'],
      valid: true,
      showMobileOrEmailError: false,
      showRequiredFieldError: false,
      mobileChecking: false,
      mobileChecked: false,
      mobileAvailable: true,
      emailChecking: false,
      emailChecked: false,
      emailAvailable: true,
      validationErrors: [], // Array to hold all validation errors
      form: {
        user_id: this.empData ? this.empData.user_id : '',
        userId: this.empData ? this.empData.userId : '',
        first_name: this.empData ? this.empData.first_name : '',
        middle_name: this.empData ? this.empData.middle_name : '',
        last_name: this.empData ? this.empData.last_name : '',
        mobile: this.empData ? this.empData.mobile : '',
        email: this.empData ? this.empData.email : undefined,
        employee_id: this.empData ? this.empData.employee_id : '',
        employee_type: this.empData ? this.empData.employee_type : '',
        department: this.empData ? this.empData.department : '',
        manager_id: this.empData ? this.empData.manager_id : '',
        designation: this.empData ? this.empData.designation : '',
        salary: this.empData ? this.empData.salary : undefined,
        ifsc_code: this.empData ? this.empData.ifsc_code : '',
        account_number: this.empData ? this.empData.account_number : '',
        is_corporate_expense_enabled: this.empData
          ? this.empData.is_corporate_expense_enabled
          : false,
        max_corporate_expense_limit: this.empData ? this.empData.max_corporate_expense_limit : 0,
        // Additional Info fields
        bankName: this.empData
          ? this.empData?.employmentDetails?.bankName || this.empData.bankName
          : '',
        workLocation: this.empData
          ? this.empData?.employmentDetails?.workLocation || this.empData.workLocation
          : '',
        joiningDate: this.empData
          ? this.empData?.employmentDetails?.joiningDate || this.empData.joiningDate
          : '',
        exitDate: this.empData
          ? this.empData?.employmentDetails?.exitDate || this.empData.exitDate
          : '',
        ifscCode: this.empData
          ? this.empData?.employmentDetails?.ifscCode || this.empData.ifscCode
          : '',
        bankAccountNumber: this.empData
          ? this.empData?.employmentDetails?.bankAccountNumber || this.empData.bankAccountNumber
          : '',
        panNumber: this.empData
          ? this.empData?.employmentDetails?.panNumber || this.empData.panNumber
          : '',
        aadharNumber: this.empData
          ? this.empData?.employmentDetails?.aadharNumber || this.empData.aadharNumber
          : '',
        effectiveDate: this.empData
          ? this.empData?.employmentDetails?.effectiveDate || this.empData.effectiveDate
          : '',
        pfNumber: this.empData
          ? this.empData?.employmentDetails?.pfNumber || this.empData.pfNumber
          : '',
        pfUAN: this.empData ? this.empData?.employmentDetails?.pfUAN || this.empData.pfUAN : '',
        esicNumber: this.empData
          ? this.empData?.employmentDetails?.esicNumber || this.empData.esicNumber
          : '',
        // Add gender field
        gender: this.empData ? this.empData.gender : '',
        // Add marital status field
        maritalStatus: this.empData ? this.empData.maritalStatus : '',
        // Add DOB field
        dob: this.empData ? this.empData.dob : '',
        employmentDetails: {
          joiningDetails: {
            joinedOn: '',
            joinedAs: '',
            confirmationDate: '',
            status: '',
            probationPeriod: '',
            noticePeriod: '',
            referredBy: ''
          },
          workHistory: [],
          identityDetails: [],
          education: [],
          backgroundVerification: {
            status: '',
            verifiedBy: '',
            verifiedOn: '',
            remarks: ''
          },
          remarks: '',
          contact: {
            name: '',
            address: {
              addressLine1: '',
              city: '',
              state: '',
              country: '',
              pinCode: ''
            },
            contactNumber: '',
            email: ''
          },
          emergencyContact: {
            name: '',
            address: {
              addressLine1: '',
              city: '',
              state: '',
              country: '',
              pinCode: ''
            },
            contactNumber: '',
            email: ''
          }
        },
        address: {
          sameAsPermanent:
            this.empData?.is_both_address_same !== undefined
              ? this.empData?.is_both_address_same
              : true,
          permanent: {
            addressLine1:
              this.empData?.permanent_address_line_1 || this.empData?.addressLine1 || '',
            addressLine2: this.empData?.permanent_address_line_2 || '',
            city: this.empData?.permanent_address_city || this.empData?.city || '',
            state: this.empData?.permanent_address_state || this.empData?.state || '',
            country: this.empData?.permanent_address_country || this.empData?.country || '',
            pinCode: this.empData?.permanent_address_pincode || this.empData?.pinCode || ''
          },
          current: {
            addressLine1: this.empData?.current_address_line_1 || '',
            addressLine2: this.empData?.current_address_line_2 || '',
            city: this.empData?.current_address_city || '',
            state: this.empData?.current_address_state || '',
            country: this.empData?.current_address_country || '',
            pinCode: this.empData?.current_address_pincode || ''
          }
        }
      }
    }
  },
  mounted() {
    this.getEmployeeTypes()
    this.fetchDepartments()
    // Initialize selected values based on empData if in edit mode
    if (this.isEditMode && this.empData) {
      this.$nextTick(() => {
        this.initializeEditData()
      })
    }
  },
  created() {
    // Create debounced functions
    this.debouncedCheckMobileAvailability = debounce(this.checkMobileAvailability, 500)
    this.debouncedCheckEmailAvailability = debounce(this.checkEmailAvailability, 500)
    this.fetchDepartments()
    if (this.isEditMode && this.empData) {
      this.$nextTick(() => {
        this.initializeEditData()
      })
    }
  },
  watch: {
    managersList: {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal.length > 0 && this.isEditMode && this.empData) {
          this.$nextTick(() => {
            this.setSelectedValuesForEdit()
          })
        }
      }
    },
    roleList: {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal.length > 0 && this.isEditMode && this.empData) {
          this.$nextTick(() => {
            this.setSelectedValuesForEdit()
          })
        }
      }
    },
    branches: {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal.length > 0 && this.isEditMode && this.empData) {
          this.$nextTick(() => {
            this.setSelectedValuesForEdit()
          })
        }
      }
    },
    teams: {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal.length > 0 && this.isEditMode && this.empData) {
          this.$nextTick(() => {
            this.setSelectedValuesForEdit()
          })
        }
      }
    },
    empData: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.$nextTick(() => {
            this.initializeEditData()
          })
        }
      }
    },
    // Add watchers for department and designation selection
    selectedDepartment: {
      handler(newVal) {
        if (newVal && this.departments.length > 0) {
          const department = this.departments.find((d) => d._id === newVal)
          if (department) {
            this.form.department = department._id
          }
        } else {
          this.form.department = ''
        }
      }
    },
    selectedDesignation: {
      handler(newVal) {
        if (newVal && this.designations.length > 0) {
          const designation = this.designations.find((d) => d._id === newVal)
          if (designation) {
            this.form.designation = designation._id
          }
        } else {
          this.form.designation = ''
        }
      }
    },
    // Add a watcher for when the component becomes visible
    isVisible: {
      immediate: true,
      handler(newVal) {
        // When the component becomes visible and we're in edit mode, re-initialize the data
        if (newVal && this.isEditMode && this.empData) {
          this.initializeEditData()
        }
      }
    },
    'form.mobile': {
      handler(newVal) {
        if (newVal && newVal.toString().trim() !== '') {
          this.showMobileOrEmailError = false
        }
        this.mobileChecked = false
        this.mobileAvailable = true
        this.updateValidationErrors()
      }
    },
    'form.email': {
      handler(newVal) {
        if (newVal && newVal.toString().trim() !== '') {
          this.showMobileOrEmailError = false
        }
        this.emailChecked = false
        this.emailAvailable = true
        this.updateValidationErrors()
      }
    },
    // Add watcher for selectedBranch to auto-populate workLocation
    selectedBranch: {
      handler(newVal) {
        if (newVal) {
          const branch = this.branches.find((b) => b._id === newVal)
          if (branch?.branchLocation) {
            this.form.workLocation = branch?.branchLocation?.addressLine1 || ''
          }
        } else if (!newVal) {
          this.form.workLocation = ''
        }
      }
    },
    mobileAvailable: {
      handler() {
        this.updateValidationErrors()
      }
    },
    emailAvailable: {
      handler() {
        this.updateValidationErrors()
      }
    },
    showMobileOrEmailError: {
      handler() {
        this.updateValidationErrors()
      }
    }
  },
  computed: {
    isDisabled() {
      return this.disabled || false
    },
    employeeTabs() {
      const tabs = [
        {
          label: this.isEditMode ? 'Edit Employee' : 'Add Employee',
          key: 'addemployee',
          icon: this.isEditMode ? 'mdi-pencil' : 'mdi-plus',
          component: 'EmployeeAdd'
        },
        {
          label: this.isEditMode ? 'Bulk Create' : 'Bulk Upload',
          key: 'bulk',
          icon: 'mdi-upload',
          component: 'BulkEmployeeUpload'
        }
      ]
      return tabs
    },
    fullNameManagers() {
      return this.managersList.map((manager) => ({
        ...manager,
        fullName:
          manager._id === this.userData._id
            ? 'Self'
            : `${manager.firstName} ${manager.lastName} - ${manager.wfmRole.role}`
      }))
    },
    // Check if we're editing an existing employee
    isEditMode() {
      return this.empData && this.empData.user_id
    },

    // Check if mobile field should be disabled in edit mode
    isMobileFieldDisabled() {
      if (!this.isEditMode) return false
      return !!this.empData?.mobile
    },

    // Check if email field should be disabled in edit mode
    isEmailFieldDisabled() {
      if (!this.isEditMode) return false
      return !!this.empData?.email
    },
    isVisible() {
      return !!this.empData
    },
    filteredTeams() {
      const empBranch = this.selectedBranch?._id || this.selectedBranch
      if (!empBranch) {
        return []
      }
      const teams = this.teams.filter((team) => team.branch === empBranch)
      return teams
    },
    filteredDesignations() {
      if (!this.selectedDepartment) {
        return []
      }
      const departments = this.designations.filter(
        (d) => d.department?._id === this.selectedDepartment
      )
      return departments
    }
  },
  methods: {
    handleSaveButtonVisibility(shouldShow) {
      if (this.activeType === 'bulk') {
        this.isSaveButtonEnabled = shouldShow
      }
    },
    handleTabChange(activeTab) {
      this.activeType = activeTab

      // Handle save button visibility for different tabs
      if (activeTab === 'addemployee') {
        this.isSaveButtonEnabled = true
      } else if (activeTab === 'bulk') {
        // For bulk tab, let GenericBulkUpload control the visibility
        // Initial state will be set by GenericBulkUpload's mounted hook
      }
    },

    getLoggedInUserId() {
      return this.userData
    },
    initializeSelectedManager() {
      this.selectedManager = this.getLoggedInUserId()
    },
    // Initialize selected values based on empData when in edit mode
    initializeEditData() {
      if (!this.empData) return

      // Set form values based on empData
      this.form.user_id = this.empData.user_id || ''
      this.form.userId = this.empData.userId || ''
      this.form.first_name = this.empData.first_name || ''
      this.form.middle_name = this.empData.middle_name || ''
      this.form.last_name = this.empData.last_name || ''
      this.form.mobile = this.empData.mobile || ''
      this.form.gender = this.empData.gender || ''
      this.form.maritalStatus = this.empData.maritalStatus || ''
      this.form.dob = this.empData.dob || ''
      this.form.email = this.empData.email || undefined
      this.form.employee_id = this.empData.employee_id || ''
      this.form.employee_type = this.empData.employee_type || ''
      this.form.department = this.empData.department || ''
      this.form.manager_id = this.empData.manager_id || ''
      this.form.designation = this.empData.designation || ''
      this.form.salary = this.empData.salary || undefined
      this.form.ifsc_code = this.empData.ifsc_code || ''
      this.form.account_number = this.empData.account_number || ''
      this.form.is_corporate_expense_enabled = this.empData.is_corporate_expense_enabled || false
      this.form.max_corporate_expense_limit = this.empData.max_corporate_expense_limit || 0
      // Additional Info fields
      this.form.bankName = this.empData?.employmentDetails?.bankName || this.empData.bankName || ''
      this.form.workLocation =
        this.empData?.employmentDetails?.workLocation || this.empData.workLocation || ''
      this.form.joiningDate =
        this.empData?.employmentDetails?.joiningDate || this.empData.joiningDate || ''
      this.form.exitDate = this.empData?.employmentDetails?.exitDate || this.empData.exitDate || ''
      this.form.ifscCode = this.empData?.employmentDetails?.ifscCode || this.empData.ifscCode || ''
      this.form.bankAccountNumber =
        this.empData?.employmentDetails?.bankAccountNumber || this.empData.bankAccountNumber || ''
      this.form.panNumber =
        this.empData?.employmentDetails?.panNumber || this.empData.panNumber || ''
      this.form.aadharNumber =
        this.empData?.employmentDetails?.aadharNumber || this.empData.aadharNumber || ''
      this.form.effectiveDate =
        this.empData?.employmentDetails?.effectiveDate || this.empData.effectiveDate || ''
      this.form.pfNumber = this.empData?.employmentDetails?.pfNumber || this.empData.pfNumber || ''
      this.form.pfUAN = this.empData?.employmentDetails?.pfUAN || this.empData.pfUAN || ''
      this.form.esicNumber =
        this.empData?.employmentDetails?.esicNumber || this.empData.esicNumber || ''

      // Set address information
      this.form.address = {
        sameAsPermanent:
          this.empData.is_both_address_same !== undefined
            ? this.empData.is_both_address_same
            : true,
        permanent: {
          addressLine1: this.empData.permanent_address_line_1 || this.empData.addressLine1 || '',
          addressLine2: this.empData.permanent_address_line_2 || '',
          city: this.empData.permanent_address_city || this.empData.city || '',
          state: this.empData.permanent_address_state || this.empData.state || '',
          country: this.empData.permanent_address_country || this.empData.country || '',
          pinCode: this.empData.permanent_address_pincode || this.empData.pinCode || ''
        },
        current: {
          addressLine1: this.empData.current_address_line_1 || '',
          addressLine2: this.empData.current_address_line_2 || '',
          city: this.empData.current_address_city || '',
          state: this.empData.current_address_state || '',
          country: this.empData.current_address_country || '',
          pinCode: this.empData.current_address_pincode || ''
        }
      }

      if (this.empData && this.empData.employmentDetails) {
        // Deep copy employment details
        const ed = this.empData.employmentDetails
        this.form.employmentDetails = {
          joiningDetails: ed.joiningDetails
            ? {
                ...ed.joiningDetails,
                referredBy:
                  ed.joiningDetails.referredBy && typeof ed.joiningDetails.referredBy === 'object'
                    ? ed.joiningDetails.referredBy._id
                    : ed.joiningDetails.referredBy
              }
            : this.form.employmentDetails.joiningDetails,
          workHistory: ed.workHistory || [],
          identityDetails: ed.identityDetails || [],
          education: ed.education || [],
          backgroundVerification:
            ed.backgroundVerification || this.form.employmentDetails.backgroundVerification,
          remarks: ed.remarks || '',
          contact: ed.contact
            ? {
                ...ed.contact,
                address: ed.contact.address || this.form.employmentDetails.contact.address
              }
            : this.form.employmentDetails.contact,
          emergencyContact: ed.emergencyContact
            ? {
                ...ed.emergencyContact,
                address:
                  ed.emergencyContact.address ||
                  this.form.employmentDetails.emergencyContact.address
              }
            : this.form.employmentDetails.emergencyContact
        }
      }

      // Set selected values for management assignment
      this.setSelectedValuesForEdit()
      // if (this.empData.department && this.departments.length > 0) {
      //   let department = this.departments.find((d) => d._id === this.empData.department?._id)

      //   console.log(this.empData.department, 'aa', department)
      //   if (department) {
      //     this.selectedDepartment = department
    },

    setSelectedValuesForEdit() {
      if (!this.isEditMode || !this.empData) return

      // Set selected manager
      if (this.empData.manager_id && this.managersList && this.managersList.length > 0) {
        const manager = this.managersList.find((m) => m._id === this.empData.manager_id)
        if (manager) {
          this.selectedManager = manager
        }
      }

      if (this.empData.wfmRoleId && this.roleList && this.roleList.length > 0) {
        const role = this.roleList.find((r) => r._id === this.empData.wfmRoleId)
        if (role) {
          this.selectedRole = role
        }
      }
      // console.log(this.empData.branch, this.branches)
      // Set selected branch
      if (this.empData.branch && this.branches && this.branches.length > 0) {
        const branch = this.branches.find((b) => b._id === this.empData.branch)
        if (branch) {
          this.selectedBranch = branch
          // Also set work location from branch address
          if (branch.branchLocation) {
            this.form.workLocation = branch.branchLocation.addressLine1 || ''
          }
        }
      }

      // Set selected team
      if (this.empData.team && this.teams && this.teams.length > 0) {
        const team = this.teams.find((t) => t._id === this.empData.team)
        if (team) {
          this.selectedTeam = team
        }
      }

      this.selectedDepartment = this.empData.department?._id || null
      this.selectedDesignation = this.empData.designation?._id || null

      this.fetchDesignationsByDepartment(this.selectedDepartment).then(() => {
        if (this.empData.designation && this.designations.length > 0) {
          let designation = this.designations.find((d) => d._id === this.empData.designation?._id)

          if (designation) {
            this.selectedDesignation = designation
          }
        }
      })
    },
    addItem(type) {
      if (type === 'workHistory') {
        this.form.employmentDetails.workHistory.push({
          employerName: '',
          designation: '',
          joiningDate: '',
          exitDate: '',
          reasonForLeaving: ''
        })
      } else if (type === 'education') {
        this.form.employmentDetails.education.push({
          degree: '',
          institute: '',
          grade: '',
          qualificationArea: '',
          from: '',
          to: '',
          remarks: ''
        })
      } else if (type === 'identityDetails') {
        this.form.employmentDetails.identityDetails.push({
          idType: '',
          idNumber: '',
          nameAsperId: '',
          expiryDate: '',
          issuedBy: '',
          issueDate: '',
          isVerfied: false,
          verifiedBy: '',
          verifiedOn: '',
          accountNumber: '',
          ifscCode: '',
          bankName: '',
          branchName: ''
        })
      }
    },
    removeItem(type, index) {
      if (this.form.employmentDetails[type]) {
        this.form.employmentDetails[type].splice(index, 1)
      }
    },
    handleSelectedPlaceCurrent(place) {
      this.currentSelectedAddress = place.formatted_address
      this.form.address.current.addressLine1 = this.currentSelectedAddress
      this.form.address.current.city = place.city
      this.form.address.current.state = place.state
      this.form.address.current.country = place.country
      this.form.address.current.pinCode = place.pincode
    },
    handleSelectedPlace(place) {
      // console.log('place', place)
      this.permanetSelectedAddress = place.formatted_address
      this.form.address.permanent.addressLine1 = this.permanetSelectedAddress
      this.form.address.permanent.city = place.city
      this.form.address.permanent.state = place.state
      this.form.address.permanent.country = place.country
      this.form.address.permanent.pinCode = place.pincode
    },
    getEmployeeTypes() {
      this.employeeTypes = employeeTypes.map((type) => ({ name: type }))
    },
    // Add methods to fetch departments and designations
    async fetchDepartments() {
      try {
        const response = await this.$axios.get(axiosUrl.departments.getDepartments)
        this.departments = response.data.departments || []
      } catch (error) {
        console.error('Error fetching departments:', error)
        this.departments = []
      }
    },
    async fetchDesignationsByDepartment(departmentId) {
      try {
        if (!departmentId) {
          console.log('Department ID is required')
          return
        }
        const response = await this.$axios.get(
          `${axiosUrl.designations.getDesignations}?department=${departmentId}`
        )
        this.designations = response.data.designations || []
        // Reset selected designation when department changes
        this.selectedDesignation = null
        this.form.designation = ''
      } catch (error) {
        console.error('Error fetching designations:', error)
        this.designations = []
      }
    },
    // Watch for department changes to load corresponding designations
    onDepartmentChange(departmentId) {
      if (departmentId) {
        this.fetchDesignationsByDepartment(departmentId)
      } else {
        this.designations = []
        this.selectedDesignation = null
        this.form.designation = ''
      }
    },
    toasterError(msg) {
      this.$toast.error(msg)
    },
    // Mobile and email availability checking methods
    async checkMobileAvailability() {
      // Reset mobile validation state
      this.mobileChecked = false
      this.mobileAvailable = true // Reset to default
      if (!this.form.mobile || this.form.mobile.length !== 10 || !/^\d+$/.test(this.form.mobile)) {
        return
      }

      this.mobileChecking = true

      try {
        const res = await this.$axios.post('/cualitywork/mobile-availability', {
          mobile: Number(this.form.mobile)
        })

        this.mobileAvailable = res.data?.available
        this.mobileChecked = true

        if (!this.mobileAvailable) {
          this.toasterError('This mobile number is already registered')
        }
      } catch (error) {
        this.mobileAvailable = error.response?.data?.available || false
        this.mobileChecked = true
      } finally {
        this.mobileChecking = false
      }
    },
    async checkEmailAvailability() {
      // Reset email validation state
      this.emailChecked = false
      this.emailAvailable = true // Reset to default

      // Validate email format first
      if (!this.form.email || !/.+@.+\..+/.test(this.form.email)) {
        return
      }

      this.emailChecking = true

      try {
        const res = await this.$axios.post('/cualitywork/email-availability', {
          email: this.form.email
        })

        this.emailAvailable = res.data?.available
        this.emailChecked = true

        if (!this.emailAvailable) {
          this.toasterError('This email is already registered')
        }
      } catch (error) {
        this.emailAvailable = error.response?.data?.available || false
        this.emailChecked = true
      } finally {
        this.emailChecking = false
      }
    },
    mobileOrEmailRequired(v) {
      // If we're editing an existing employee, this validation might not be needed
      if (this.isEditMode) {
        return true
      }

      // For new employees, either mobile or email is required
      const hasMobile = this.form.mobile && this.form.mobile.toString().trim() !== ''
      const hasEmail = this.form.email && this.form.email.toString().trim() !== ''

      // Set error flag if neither is provided (but don't show it immediately)
      this.showMobileOrEmailError = !hasMobile && !hasEmail
      return hasMobile || hasEmail
    },
    async saveEmployee() {
      if (this.activeType === 'bulk') {
        this.$refs.bulkUploader?.processFile()
        return
      }

      // Set flag to show required field error when save is clicked
      this.showRequiredFieldError = true

      // Run our custom validation for mobile or email
      const mobileOrEmailValid = this.mobileOrEmailRequired()

      if (!this.$refs.form.validate() || typeof mobileOrEmailValid === 'string') {
        this.toasterError('Please fix validation errors before saving')
        // Update validation errors to show the required field message
        this.updateValidationErrors()
        return
      }

      // Reset the flag if validation passes
      this.showRequiredFieldError = false

      // Check mobile and email availability before saving
      if (!this.isEditMode) {
        if (
          (this.form.mobile && !this.mobileAvailable) ||
          (this.form.email && !this.emailAvailable)
        ) {
          this.toasterError('Please fix availability issues before saving')
          return
        }
      }

      try {
        if (this.isEditMode) {
          await this.updateEmployeeDetails()
          // await this.updateEmployeeRelationships()
          const successMessage = 'Employee updated successfully'
          this.$toast.success(successMessage)
          const updatedUser = {
            user_id: this.form.user_id,
            userId: this.form.userId,
            first_name: this.form.first_name,
            last_name: this.form.last_name || undefined,
            mobile: this.form.mobile || undefined,
            email: this.form.email || undefined,
            employee_id: this.form.employee_id,
            employee_type: this.form.employee_type,
            department: this.form.department,
            designation: this.form.designation,
            salary: this.form.salary || undefined,
            manager: this.selectedManager || null,
            selectedRole: this.selectedRole || null,
            selectedTeam: this.selectedTeam || null,
            selectedBranch: this.selectedBranch || null
          }

          this.$emit('refreshEmployees', updatedUser)
          this.resetForm()
        } else {
          // Create new employee
          const currentAddrs = this.form.address.sameAsPermanent
            ? this.form.address.permanent
            : this.form.address.current

          const userAddress = {
            address_type: 'BILLING',
            addressLine1: currentAddrs.addressLine1,
            addressLine2: currentAddrs.addressLine2 || '',
            city: currentAddrs.city,
            state: currentAddrs.state,
            country: currentAddrs.country,
            pinCode: currentAddrs.pinCode
          }

          // Create a clean payload excluding empty optional fields
          const payload = {
            first_name: this.form.first_name,
            employee_id: this.form.employee_id,
            employee_type: this.form.employee_type,
            userAddress,
            organization_id: this.userData.organization_id
          }

          // Only include optional fields if they have values
          if (this.form.middle_name && this.form.middle_name.trim() !== '') {
            payload.middle_name = this.form.middle_name
          }

          if (this.form.last_name && this.form.last_name.trim() !== '') {
            payload.last_name = this.form.last_name
          }

          if (this.form.mobile && this.form.mobile.toString().trim() !== '') {
            payload.mobile = this.form.mobile
          }

          if (this.form.email && this.form.email.toString().trim() !== '') {
            payload.email = this.form.email
          }

          // Add gender field
          if (this.form.gender && this.form.gender.trim() !== '') {
            payload.gender = this.form.gender
          }
          // Add marital status field
          if (this.form.maritalStatus && this.form.maritalStatus.trim() !== '') {
            payload.maritalStatus = this.form.maritalStatus
          }

          // Add DOB field
          if (this.form.dob && this.form.dob.trim() !== '') {
            payload.dob = this.form.dob
          }

          // Handle department and designation
          if (this.selectedDepartment && this.departments.length > 0) {
            const department = this.departments.find((d) => d._id === this.selectedDepartment)
            if (department) {
              payload.department = department?._id
            }
          }
          // else if (this.form.department && this.form.department.trim() !== '') {
          //   payload.department = this.form.department
          // }

          if (this.selectedDesignation && this.designations.length > 0) {
            const designation = this.designations.find((d) => d._id === this.selectedDesignation)
            if (designation) {
              payload.designation = designation._id
            }
          }
          // else if (this.form.designation && this.form.designation.trim() !== '') {
          //   payload.designation = this.form.designation
          // }

          if (this.form.salary && this.form.salary.toString().trim() !== '') {
            payload.salary = this.form.salary
          }

          // Additional Info fields
          if (this.form.bankName && this.form.bankName.trim() !== '') {
            payload.bankName = this.form.bankName
          }

          if (this.form.ifscCode && this.form.ifscCode.trim() !== '') {
            payload.ifscCode = this.form.ifscCode
          } else if (this.form.ifsc_code && this.form.ifsc_code.trim() !== '') {
            payload.ifscCode = this.form.ifsc_code
          }

          if (this.form.bankAccountNumber && this.form.bankAccountNumber.trim() !== '') {
            payload.bankAccountNumber = this.form.bankAccountNumber
          } else if (this.form.account_number && this.form.account_number.trim() !== '') {
            payload.bankAccountNumber = this.form.account_number
          }

          if (this.form.panNumber && this.form.panNumber.trim() !== '') {
            payload.panNumber = this.form.panNumber
          }

          if (this.form.aadharNumber && this.form.aadharNumber.trim() !== '') {
            payload.aadharNumber = this.form.aadharNumber
          }

          if (this.form.pfNumber && this.form.pfNumber.trim() !== '') {
            payload.pfNumber = this.form.pfNumber
          }

          if (this.form.pfUAN && this.form.pfUAN.trim() !== '') {
            payload.pfUAN = this.form.pfUAN
          }

          if (this.form.esicNumber && this.form.esicNumber.trim() !== '') {
            payload.esicNumber = this.form.esicNumber
          }

          // Management fields
          if (this.form.joiningDate && this.form.joiningDate.trim() !== '') {
            payload.joiningDate = this.form.joiningDate
          }

          if (this.form.exitDate && this.form.exitDate.trim() !== '') {
            payload.exitDate = this.form.exitDate
          }

          if (this.form.workLocation && this.form.workLocation.trim() !== '') {
            payload.workLocation = this.form.workLocation
          }

          if (this.form.effectiveDate && this.form.effectiveDate.trim() !== '') {
            payload.effectiveDate = this.form.effectiveDate
          }

          // Corporate expense fields
          payload.is_corporate_expense_enabled = this.form.is_corporate_expense_enabled || false
          if (this.form.is_corporate_expense_enabled && this.form.max_corporate_expense_limit) {
            payload.max_corporate_expense_limit = this.form.max_corporate_expense_limit
          }

          // Add employmentDetails to payload
          if (this.form.employmentDetails) {
            const employmentDetails = JSON.parse(JSON.stringify(this.form.employmentDetails))
            if (employmentDetails.joiningDetails && !employmentDetails.joiningDetails.referredBy) {
              employmentDetails.joiningDetails.referredBy = null
            }
            payload.employmentDetails = employmentDetails
          }

          this.$axios.post('/user/org-employee', payload).then((res) => {
            if (res.data?.success || res.status === 201) {
              const successMessage =
                res.data.message === 'Update Success'
                  ? 'Employee updated successfully'
                  : 'Employee registered successfully'

              this.$toast.success(successMessage)
              const newUser = {
                ...res.data?.data,
                manager: this.selectedManager || null,
                selectedRole: this.selectedRole || null,
                selectedTeam: this.selectedTeam || null,
                selectedBranch: this.selectedBranch || null,
                department: this.form.department || null,
                designation: this.form.designation || null
              }

              this.$emit('refreshEmployees', newUser)
              this.resetForm()
            }
          })
        }
      } catch (err) {
        const errorMessage =
          err.response?.data?.message || 'An error occurred while saving employee'
        this.$toast.error(errorMessage)
      }
    },
    async updateEmployeeDetails() {
      const payload = {
        employee_id: this.form.employee_id,
        employee_type: this.form.employee_type,
        salary: Number(this.form.salary) || undefined,
        reportesToId: this.selectedManager?._id || undefined,
        userAddress: {}
      }

      // Handle department and designation
      if (this.selectedDepartment && this.departments.length > 0) {
        const department = this.departments.find((d) => d._id === this.selectedDepartment)
        if (department) {
          payload.department = department?._id
        }
      }

      if (this.selectedDesignation && this.designations.length > 0) {
        const designation = this.designations.find((d) => d._id === this.selectedDesignation)
        if (designation) {
          payload.designation = designation?._id
        }
      }

      if (this.form.first_name || this.form.last_name) {
        payload.first_name = this.form.first_name
        payload.last_name = this.form.last_name
      }
      if (this.form.mobile) {
        payload.mobile = this.form.mobile
      }
      if (this.form.email) {
        payload.email = this.form.email
      }

      // Add employmentDetails to payload for update
      if (this.form.employmentDetails) {
        const employmentDetails = JSON.parse(JSON.stringify(this.form.employmentDetails))
        if (employmentDetails.joiningDetails && !employmentDetails.joiningDetails.referredBy) {
          employmentDetails.joiningDetails.referredBy = null
        }
        payload.employmentDetails = employmentDetails
      }

      // Add gender field
      if (this.form.gender && this.form.gender.trim() !== '') {
        payload.gender = this.form.gender
      }
      // Add marital status field
      if (this.form.maritalStatus && this.form.maritalStatus.trim() !== '') {
        payload.maritalStatus = this.form.maritalStatus
      }

      // Add DOB field
      if (this.form.dob && this.form.dob.trim() !== '') {
        payload.dob = this.form.dob
      }

      // Additional Info fields
      if (this.form.bankName && this.form.bankName.trim() !== '') {
        payload.bankName = this.form.bankName
      }

      if (this.form.ifscCode && this.form.ifscCode.trim() !== '') {
        payload.ifscCode = this.form.ifscCode
      } else if (this.form.ifsc_code && this.form.ifsc_code.trim() !== '') {
        payload.ifscCode = this.form.ifsc_code
      }

      if (this.form.bankAccountNumber && this.form.bankAccountNumber.trim() !== '') {
        payload.bankAccountNumber = this.form.bankAccountNumber
      } else if (this.form.account_number && this.form.account_number.trim() !== '') {
        payload.bankAccountNumber = this.form.account_number
      }

      if (this.form.panNumber && this.form.panNumber.trim() !== '') {
        payload.panNumber = this.form.panNumber
      }

      if (this.form.aadharNumber && this.form.aadharNumber.trim() !== '') {
        payload.aadharNumber = this.form.aadharNumber
      }

      if (this.form.pfNumber && this.form.pfNumber.trim() !== '') {
        payload.pfNumber = this.form.pfNumber
      }

      if (this.form.pfUAN && this.form.pfUAN.trim() !== '') {
        payload.pfUAN = this.form.pfUAN
      }

      if (this.form.esicNumber && this.form.esicNumber.trim() !== '') {
        payload.esicNumber = this.form.esicNumber
      }

      // Management fields
      if (this.form.joiningDate && this.form.joiningDate.trim() !== '') {
        payload.joiningDate = this.form.joiningDate
      }

      if (this.form.exitDate && this.form.exitDate.trim() !== '') {
        payload.exitDate = this.form.exitDate
      }

      if (this.form.workLocation && this.form.workLocation.trim() !== '') {
        payload.workLocation = this.form.workLocation
      }

      if (this.form.effectiveDate && this.form.effectiveDate.trim() !== '') {
        payload.effectiveDate = this.form.effectiveDate
      }

      const currentAddrs = this.form.address.sameAsPermanent
        ? this.form.address.permanent
        : this.form.address.current

      if (currentAddrs.addressLine1) {
        payload.userAddress = {
          address_type: 'BILLING',
          addressLine1: currentAddrs.addressLine1,
          addressLine2: currentAddrs.addressLine2 || '',
          city: currentAddrs.city,
          state: currentAddrs.state,
          country: currentAddrs.country,
          pinCode: currentAddrs.pinCode
        }
      }
      await this.$axios.put(`/user/employeeDetails/${this.form.user_id}`, payload)
    },
    // Update employee relationships using the same approach as the previous implementation
    async updateEmployeeRelationships() {
      const empId = this.form.user_id

      if (this.selectedManager) {
        await this.$axios.patch(`/users/${empId}?action=UPDATE_REPORTING_TO`, {
          reportesToId: this.selectedManager._id || null
        })
      }

      if (this.selectedRole) {
        await this.$axios.patch(`/users/${empId}`, {
          wfmRoleId: this.selectedRole._id
        })
      }

      if (this.selectedBranch || this.selectedTeam) {
        const userInfo = {}
        if (this.selectedBranch) {
          userInfo.branch = this.selectedBranch
        }
        if (this.selectedTeam) {
          userInfo.team = this.selectedTeam._id
        }

        await this.$axios.patch(`/users/${empId}?action=UPDATE_USER_INFO`, {
          userInfo
        })
      }
    },
    resetForm() {
      this.form = {
        user_id: '',
        userId: '',
        first_name: '',
        middle_name: '',
        last_name: '',
        mobile: '',
        email: undefined,
        employee_id: '',
        employee_type: '',
        department: '',
        manager_id: '',
        designation: '',
        salary: undefined,
        ifsc_code: '',
        account_number: '',
        is_corporate_expense_enabled: false,
        max_corporate_expense_limit: 0,
        // Additional Info fields
        bankName: '',
        workLocation: '',
        joiningDate: '',
        exitDate: '',
        ifscCode: '',
        bankAccountNumber: '',
        panNumber: '',
        aadharNumber: '',
        effectiveDate: '',
        pfNumber: '',
        pfUAN: '',
        esicNumber: '',
        // Add gender field
        gender: '',
        // Add DOB field
        dob: '',
        address: {
          sameAsPermanent: true,
          permanent: {
            addressLine1: '',
            addressLine2: '',
            city: '',
            state: '',
            country: '',
            pinCode: ''
          },
          current: {
            addressLine1: '',
            addressLine2: '',
            city: '',
            state: '',
            country: '',
            pinCode: ''
          }
        }
      }
      // Only reset selection values if not in edit mode
      if (!this.isEditMode) {
        this.selectedManager = null
        this.selectedRole = null
        this.selectedBranch = null
        this.selectedTeam = null
        // Reset department and designation selections
        this.selectedDepartment = null
        this.selectedDesignation = null
        // Reset workLocation when form is reset
        this.form.workLocation = ''
      }
    },
    cancel() {
      if (this.$refs.form) {
        this.$refs.form.resetValidation()
      }
      // Don't reset form data if in edit mode, just close the dialog
      if (!this.isEditMode) {
        this.resetForm()
      }
      this.$emit('cancelDrawer')
    },
    handleBulkUploadComplete(data) {
      this.$emit('refreshEmployees')
      this.$emit('cancelDrawer')
    },
    updateValidationErrors() {
      const errors = []
      if (this.showRequiredFieldError && this.showMobileOrEmailError) {
        errors.push('Either Mobile or Email are required')
      }
      if (this.mobileChecked && !this.mobileAvailable) {
        errors.push('This mobile number is already registered. Please try another.')
      }

      if (this.emailChecked && !this.emailAvailable) {
        errors.push('This email is already registered. Please try another.')
      }

      this.validationErrors = errors
    }
  }
}
</script>

<style scoped>
.employee-form-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #f8f9fa;
  border-radius: 8px 8px 0 0;
}

.form-content {
  padding: 10px;
  max-height: 70vh;
  overflow-y: auto;
  background-color: #f8f9fa;
}

.form-section {
  margin-bottom: 10px;
}

.form-content-border {
  width: 100%;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 16px;
  background-color: #ffffff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.02);
}

.section-title {
  font-size: 1rem;
  font-weight: 500;
  color: #21427d;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
}

.section-content {
  padding-left: 8px;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  padding: 16px 24px;
  background-color: #f8f9fa;
  border-radius: 0 0 8px 8px;
  gap: 12px;
}

.cancel-btn {
  min-width: 100px;
  text-transform: none;
  font-weight: 500;
}

.save-btn {
  min-width: 140px;
  text-transform: none;
  font-weight: 500;
}

/* Ensure consistent field spacing */
:deep(.v-text-field) {
  margin-bottom: 0;
}

:deep(.v-select) {
  margin-bottom: 0;
}

/* Dark theme support */
:deep(.theme--dark) .form-content {
  background-color: #2a2a2a;
}

:deep(.theme--dark) .form-content-border {
  background-color: #2d2d2d;
  border-color: #444;
}

:deep(.theme--dark) .section-title {
  color: #8cb6e1;
}

:deep(.theme--dark) .form-actions {
  background-color: #2a2a2a;
}

:deep(.theme--dark) .employee-form-header {
  background-color: #2a2a2a;
}

/* Responsive adjustments */
@media (max-width: 600px) {
  .form-content {
    padding: 16px;
  }

  .form-section {
    margin-bottom: 16px;
  }

  .form-actions {
    flex-direction: column;
    gap: 8px;
  }

  .cancel-btn,
  .save-btn {
    width: 100%;
  }
}
</style>
